// ----- HTMLElement -----

/**
 * document.querySelector の省略形
 */
export function $<E extends HTMLElement = HTMLElement>(selector: string): E | null {
  return document.querySelector(selector)
}

/**
 * document.querySelectorAll の省略形
 */
export function $$<E extends HTMLElement = HTMLElement>(selector: string): NodeListOf<E> {
  return document.querySelectorAll(selector)
}

/**
 * Element を取得し !null なら特定の処理を実行, 実行後不要になったオブジェクトは開放
 */
export const elementHandler = (selector: string): Promise<HTMLElement> => {
  return new Promise((resolve: Function, reject: (reason?: any) => void) => {
    let elem = $(selector)
    if (!elem) {
      reject('Error: not exist element')
      return
    }
    resolve(elem)
    elem = null
  })
}

/**
 * 指定要素の削除
 */
export function removeElement(el) {
  if (!el || el === null) {
    return
  }
  if (typeof el.remove !== 'undefined') {
    el.remove()
  } else {
    if (!el.parentNode || el.parentNode === null) {
      return
    }
    el.parentNode.removeChild(el)
  }
}

/**
 * 動画の media data を取得する
 */
export const loadmetadataVideo = (src: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    let video = document.createElement('video')
    video.onloadedmetadata = () => {
      resolve(video)
    }
    video.onerror = reject
    video.src = src
  })
}

// ----- Object -----

/**
 * 文字列が JSON 形式になっているか判定する
 */
export const isJSONString = (str: string): boolean => {
  try {
    const ret = JSON.parse(str)
    return Object.prototype.toString.call(ret) === '[object Object]'
  } catch (err) {
    console.error(err)
    return false
  }
}

/**
 * 関数に対応した JSON.stringify
 */
export const jsonStringify = (obj: any): string => {
  return JSON.stringify(obj, replacer)
}
const replacer = (_, v) => {
  if (typeof v === 'function') {
    return v.toString()
  }
  return v
}

/**
 * 関数に対応した JSON.parse
 */
export const jsonParse = (str: string): any => {
  return JSON.parse(str, reviver)
}
const reviver = (_, v): any => {
  if (typeof v === 'string' && v.match(/^function/)) {
    return Function.call(window, 'return ' + v)()
  }
  return v
}

/**
 * オブジェクトの比較
 */
export const equalObject = (obj1: any, obj2: any): boolean => {
  if (toString.call(obj1) !== '[object Object]' || toString.call(obj2) !== '[object Object]') {
    return false
  }

  const map1 = sortObject(obj1)
  const map2 = sortObject(obj2)

  return JSON.stringify(map1) === JSON.stringify(map2)
}

/**
 * オブジェクトをキーでソートする
 */
export const sortObject = (object: any, options?: { reverse: boolean }): any => {
  const sorted = {}
  const keys = Object.keys(object)

  if (options && options.reverse) {
    keys.reverse()
  } else {
    keys.sort()
  }

  for (const key of keys) {
    sorted[key] = object[key]
  }
  return sorted
}

// ----- Array -----

/**
 * 指定範囲の数値の配列を作成して返す
 */
export const range = (from: number, to?: number): number[] => {
  let start
  let end
  if (to) {
    start = from
    end = to
  } else {
    start = 0
    end = from
  }
  const arr = []
  for (let i = start; i < end; i++) {
    arr.push(i)
  }
  return arr
}

/**
 * 値が連想配列か判定する
 */
export const isObject = (o: any): boolean => {
  return o instanceof Object && !Array.isArray(o)
}

// ----- Event -----
export const requestAnimationFrame =
  window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window['mozRequestAnimationFrame'] ||
  window['msRequestAnimationFrame'] ||
  window['oRequestAnimationFrame'] ||
  function(callback) {
    window.setTimeout(callback, 1000 / 60)
  }
export const cancelAnimationFrame =
  window.cancelAnimationFrame ||
  window.webkitCancelAnimationFrame ||
  window['mozCancelAnimationFrame'] ||
  window['msCancelAnimationFrame'] ||
  window['oCancelAnimationFrame']

// ----- String -----

/**
 * 文字列から Unicode Code Point に変換する
 */
export const stringToCodePoints = (str: string): string[] => {
  return str.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[^\uD800-\uDFFF]/g) || []
}

/**
 * v1 > v2
 * versionCompare('1.2.0', '1.0');  // true
 */
export const versionCompare = (v1: string, v2: string): boolean => {
  if (typeof v1 + typeof v2 !== 'stringstring') {
    return false
  }

  const v1Arr = v1.split('.')
  const v2Arr = v2.split('.')

  for (let i = 0; i < Math.max(v1Arr.length, v2Arr.length); i++) {
    if (
      (v1Arr[i] && !v2Arr[i] && parseInt(v1Arr[i], 10) > 0) ||
      parseInt(v1Arr[i], 10) > parseInt(v2Arr[i], 10)
    ) {
      return true
    } else if (
      (v2Arr[i] && !v1Arr[i] && parseInt(v2Arr[i], 10) > 0) ||
      parseInt(v1Arr[i], 10) < parseInt(v2Arr[i], 10)
    ) {
      return false
    }
  }

  return false
}

/**
 * 数値文字列に3桁区切りでカンマを入れる
 */
export const insertCommaDelimiter = (numStr: string): string => {
  if (typeof numStr === 'number') {
    numStr = numStr + ''
  }
  return numStr.replace(/(\d+)(\.\d+)?/, (_subStr, capture1, capture2) => {
    capture1 = capture1.split(/(?=(?:\d{3})+$)/).join()
    return capture2 ? capture1 + capture2.replace(/(\d{3})(?=\d)/, '$1,') : capture1
  })
}

/**
 * URL 形式の文字列からファイル名を取得する
 */
export const getFileName = (url: string): string => {
  return url.split('/').pop()
}

/**
 * ファイル名から拡張子を取得する
 */
export const getExtention = (filename: string): string => {
  const index = filename.lastIndexOf('.')
  return index === -1 ? '' : filename.slice(index)
}

// ----- Utils -----

/**
 * UUID の生成
 */
export const generateUUID = (): string => {
  const chars = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.split('')
  for (let i = 0; i < chars.length; i++) {
    if (chars[i] === 'x') chars[i] = Math.floor(Math.random() * 16).toString(16)
    else if (chars[i] === 'y') {
      chars[i] = (Math.floor(Math.random() * 4) + 8).toString(16)
    }
  }
  return chars.join('')
}

/**
 * sleep
 */
export const sleep = (msec: number): Promise<number> => {
  return new Promise(resolve => {
    setTimeout(resolve, msec)
  })
}

// --- Date ---

/**
 * 生年月日を年齢に変換する
 *
 * const age = calculateAge('yyyy-mm-dd')
 */
export const calculateAge = (birthday: string, when?: string | Date): number => {
  const birthdayDate = typeof birthday === 'string' ? new Date(birthday) : null
  if (!birthdayDate) {
    return -1
  }

  if (!when) {
    when = new Date()
  } else if (typeof when === 'string') {
    when = new Date(when)
  }

  // birthday と when の年を揃えて差分を計算し bn が大きいなら誕生日を迎えていない
  const bn = new Date(birthdayDate.getTime()).setFullYear(256)
  const wn = new Date(when.getTime()).setFullYear(256)

  const step = wn < bn ? 1 : 0
  // 誕生日を迎えていないなら -1
  return when.getFullYear() - birthdayDate.getFullYear() - step
}

// ----- Smartphone -----
export const uaIsMobile = /(Mobile)/.test(navigator.userAgent)
export const uaIsSmartPhone = /(iPhone|Android)/.test(navigator.userAgent) && uaIsMobile
export const uaIsAndroid = /Android/.test(navigator.userAgent)
export const uaIsIOS = /iP(hone|(o|a)d)/.test(navigator.userAgent)
export const uaIsIPad = /(iPad)/.test(navigator.userAgent)
export const uaIsTablet = !uaIsSmartPhone || (uaIsIPad && uaIsAndroid)
// iphone x or xs
export const isIPhoneX =
  uaIsIOS &&
  window.devicePixelRatio &&
  window.devicePixelRatio === 3 &&
  window.screen.width === 375 &&
  window.screen.height === 812
export const isIPhoneXSMax =
  uaIsIOS &&
  window.devicePixelRatio &&
  window.devicePixelRatio === 3 &&
  window.screen.width === 414 &&
  window.screen.height === 896
export const isIPhoneXR =
  uaIsIOS &&
  window.devicePixelRatio &&
  window.devicePixelRatio === 2 &&
  window.screen.width === 414 &&
  window.screen.height === 896

// ----- Media Query ----
export const mediaIsPC = window.innerWidth > 1024
export const mediaIsTablet = !mediaIsPC && window.innerWidth >= 768
export const mediaIsSmartPhone = window.innerWidth < 768

/**
 * Android のバージョン取得
 */
export const getAndroidVersion = (): string => {
  const ua = navigator.userAgent
  const index = ua.indexOf('Android')
  return index > 0 ? parseFloat(ua.slice(index + 8)) + '' : ''
}

/**
 * IOS のバージョン取得
 */
export const getIOSVersion = (): string => {
  const ua = navigator.userAgent
  ua.match(/iPhone OS (\w+){1,3}/g)
  return RegExp.$1 ? RegExp.$1.replace(/_/g, '.') : ''
}

// --- cookie ----
/**
 * Cookie を取得して Object にして返す
 */
export const getCookie = (): any => {
  const cookieStrings = document.cookie.split(';')
  const cookie = {}
  for (const c of cookieStrings) {
    const content = c.split('=')
    cookie[content[0].trim()] = decodeURIComponent(content[1])
  }
  return cookie
}

/**
 * Cookie に値をセット
 * 有効期限はデフォルトで1日
 */
export const setCookie = (key: string, value: string) => {
  const maxAge = 60 * 60 * 24
  document.cookie =
    `${encodeURIComponent(key)}=${encodeURIComponent(value)}; ` +
    `domain=${location.host}; path=/; max-age=${maxAge}`
}
